import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AdminScopeProvider } from './contexts/AdminScopeContext';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Categories from './pages/Categories';
import Products from './pages/Products';
import MenuIngredients from './pages/MenuIngredients';
import Customizations from './pages/Customizations';
import Stores from './pages/Stores';
import Drivers from './pages/Drivers'; // Import the new Drivers page
import DeliveryRulesConfigPage from './pages/DeliveryRulesConfig'; // Import the DeliveryRulesConfigPage
import Promotions from './pages/Promotions'; // Import the Promotions page
import Orders from './pages/Orders'; // Import the Orders page
import CustomerAnalytics from './pages/CustomerAnalytics'; // Import the CustomerAnalytics page
import SalesStatistics from './pages/SalesStatistics';
import Customers from './pages/Customers';
import Settings from './pages/Settings';
import Layout from './components/Layout'; // Import the Layout component
import { Box, CircularProgress } from '@mui/material';

// Route protégée qui vérifie si l'utilisateur est connecté
const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return currentUser ? <Layout title="Admin Panel">{children}</Layout> : <Navigate to="/login" />; // Wrap children with Layout
};

// Route publique qui redirige vers le tableau de bord si l'utilisateur est déjà connecté
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return !currentUser ? <>{children}</> : <Navigate to="/dashboard" />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
      {/* Ajoutez d'autres routes ici au fur et à mesure que vous créez les composants */}
      <Route path="/categories" element={<PrivateRoute><Categories /></PrivateRoute>} />
      <Route path="/products" element={<PrivateRoute><Products /></PrivateRoute>} />
      <Route path="/menu-ingredients" element={<PrivateRoute><MenuIngredients /></PrivateRoute>} />
      <Route path="/customizations" element={<PrivateRoute><Customizations /></PrivateRoute>} />
      <Route path="/promotions" element={<PrivateRoute><Promotions /></PrivateRoute>} />
      <Route path="/stores" element={<PrivateRoute><Stores /></PrivateRoute>} />
      <Route path="/drivers" element={<PrivateRoute><Drivers /></PrivateRoute>} /> {/* Add the new route for Drivers */}
      <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} /> {/* Add the new route for Orders */}
      <Route path="/sales-statistics" element={<PrivateRoute><SalesStatistics /></PrivateRoute>} />
      <Route path="/customers" element={<PrivateRoute><Customers /></PrivateRoute>} />
      <Route path="/customer-analytics" element={<PrivateRoute><CustomerAnalytics /></PrivateRoute>} /> {/* Add the new route for Customer Analytics */}
      <Route path="/delivery-rules" element={<PrivateRoute><DeliveryRulesConfigPage /></PrivateRoute>} /> {/* Add the new route for Delivery Rules */}
      <Route path="/admins" element={<PrivateRoute><Navigate to="/settings" replace /></PrivateRoute>} />
      <Route path="/store-operators" element={<PrivateRoute><Navigate to="/settings" replace /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AdminScopeProvider>
          <AppRoutes />
        </AdminScopeProvider>
      </AuthProvider>
    </Router>
  );
};

export default App;
